/**
 * Personal "Time Off" card for /profile.
 *
 * Two slots:
 *   1. Year stats (vacation/sick days used + pending) — at-a-glance for the user.
 *   2. Create + history — submit a new vacation/sick request, see own past
 *      requests with status, edit-or-cancel while still pending.
 *
 * Managers (admin/pm_lead) review and approve from /team-calendar; this card
 * is intentionally employee-centric.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Calendar, Plane, PartyPopper, Stethoscope, Trash2, Clock, CheckCircle2, XCircle, Users } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

const TYPE_META = {
  vacation: { label: "Vacation", icon: Plane, color: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  sick: { label: "Sick leave", icon: Stethoscope, color: "bg-rose-500/20 text-rose-300 border-rose-500/30" },
};

const STATUS_META = {
  pending: { label: "Pending", color: "bg-zinc-700/40 text-zinc-300 border-zinc-600", icon: Clock },
  approved: { label: "Approved", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-red-500/20 text-red-300 border-red-500/30", icon: XCircle },
  cancelled: { label: "Cancelled", color: "bg-zinc-800 text-zinc-500 border-zinc-700", icon: XCircle },
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const MyTimeOffCard = () => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [usage, setUsage] = useState({ vacation: 0, sick: 0, holiday: 0, vacation_pending: 0, sick_pending: 0 });
  const [year] = useState(new Date().getFullYear());

  const [form, setForm] = useState({ type: "vacation", start_date: todayIso(), end_date: todayIso(), reason: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [reqRes, usageRes] = await Promise.all([
        api.get("/leave-requests", { params: { scope: "mine", status: "all" } }),
        api.get("/leave-requests/usage/me", { params: { year } }),
      ]);
      // Hide cancelled entries — a withdrawn booking (or a removed company
      // holiday) is no longer relevant and only clutters the list.
      // backlog 607974e5.
      setRequests((reqRes.data || []).filter((r) => r.status !== "cancelled"));
      setUsage(usageRes.data || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load time off");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!form.start_date || !form.end_date) {
      toast.error("Pick start and end dates");
      return;
    }
    if (form.end_date < form.start_date) {
      toast.error("End date must be on or after start date");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leave-requests", form);
      toast.success("Request submitted");
      setForm({ type: "vacation", start_date: todayIso(), end_date: todayIso(), reason: "" });
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this leave request?")) return;
    try {
      await api.delete(`/leave-requests/${id}`);
      toast.success("Request cancelled");
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    }
  };

  const fmtRange = (s, e, days) => (s === e ? `${s} · ${days}d` : `${s} → ${e} · ${days}d`);

  return (
    <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-2">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-zinc-50 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-sky-400" />
          Time Off
        </CardTitle>
        {/* Own requests live here; who ELSE is off lives on the shared calendar,
            now open to the whole company. backlog 46bb95e0 */}
        <Link
          to="/team-calendar"
          className="text-xs text-sky-400 hover:text-sky-300 inline-flex items-center gap-1"
        >
          <Users className="h-3.5 w-3.5" />
          Who else is off
        </Link>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Year stats — company days off are their own tile, not a slice of
            your vacation balance. backlog d3d32706 */}
        <div className="grid grid-cols-3 gap-3">
          <div className="p-4 rounded-lg border border-amber-500/20 bg-amber-500/5">
            <div className="flex items-center gap-2 text-xs text-amber-300/80">
              <Plane className="h-3.5 w-3.5" />
              Vacation in {year}
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-semibold text-amber-200">{usage.vacation || 0}</span>
              <span className="text-xs text-amber-300/70">days used</span>
              {usage.vacation_pending > 0 && (
                <span className="text-xs text-zinc-400">+ {usage.vacation_pending} pending</span>
              )}
            </div>
          </div>
          <div className="p-4 rounded-lg border border-rose-500/20 bg-rose-500/5">
            <div className="flex items-center gap-2 text-xs text-rose-300/80">
              <Stethoscope className="h-3.5 w-3.5" />
              Sick leave in {year}
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-semibold text-rose-200">{usage.sick || 0}</span>
              <span className="text-xs text-rose-300/70">days used</span>
              {usage.sick_pending > 0 && (
                <span className="text-xs text-zinc-400">+ {usage.sick_pending} pending</span>
              )}
            </div>
          </div>
          <div className="p-4 rounded-lg border border-indigo-500/20 bg-indigo-500/5">
            <div className="flex items-center gap-2 text-xs text-indigo-300/80">
              <PartyPopper className="h-3.5 w-3.5" />
              Company days off
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-semibold text-indigo-200">{usage.holiday || 0}</span>
              <span className="text-xs text-indigo-300/70">in {year}</span>
            </div>
          </div>
        </div>

        {/* Create form */}
        <div className="p-4 rounded-lg border border-zinc-800 bg-zinc-950/50 space-y-3">
          <div className="text-sm font-medium text-zinc-200">Request time off</div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <div>
              <Label className="text-xs text-zinc-400">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-200 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="vacation">Vacation</SelectItem>
                  <SelectItem value="sick">Sick leave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Start</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                className="bg-zinc-900 border-zinc-700 text-zinc-200 mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">End</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                className="bg-zinc-900 border-zinc-700 text-zinc-200 mt-1"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full bg-orange-600 hover:bg-orange-500 text-white"
              >
                {submitting ? "Submitting…" : "Submit"}
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Note (optional)</Label>
            <Textarea
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              placeholder="Anything PM/HR should know"
              className="bg-zinc-900 border-zinc-700 text-zinc-200 mt-1 min-h-[60px]"
            />
          </div>
        </div>

        {/* History */}
        <div>
          <div className="text-sm font-medium text-zinc-300 mb-2">Your requests</div>
          {loading ? (
            <div className="text-xs text-zinc-500">Loading…</div>
          ) : requests.length === 0 ? (
            <div className="text-xs text-zinc-500 p-3 bg-zinc-950/50 rounded border border-zinc-800">
              No requests yet.
            </div>
          ) : (
            <div className="space-y-2">
              {requests.map((r) => {
                const TypeIcon = TYPE_META[r.type]?.icon || Plane;
                const StatusIcon = STATUS_META[r.status]?.icon || Clock;
                return (
                  <div
                    key={r.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-zinc-800 bg-zinc-950/40"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <TypeIcon className="h-4 w-4 text-zinc-400 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm text-zinc-200 truncate">
                          {fmtRange(r.start_date, r.end_date, r.days_count)}
                          <span className="ml-2 text-xs text-zinc-500">
                            ({TYPE_META[r.type]?.label || r.type})
                          </span>
                        </div>
                        {r.reason && <div className="text-xs text-zinc-500 truncate">{r.reason}</div>}
                        {r.review_notes && (
                          <div className="text-xs text-amber-400/80 truncate">
                            Manager: {r.review_notes}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className={cn("text-xs", STATUS_META[r.status]?.color)}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {STATUS_META[r.status]?.label || r.status}
                      </Badge>
                      {r.status === "pending" && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleCancel(r.id)}
                          className="h-7 w-7 text-zinc-500 hover:text-red-400"
                          title="Cancel"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MyTimeOffCard;
